import { Tab, Tabs } from '@nextui-org/react-latest'
import TickIcon from '@public/static/icons/payouts/TickIcon'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'

type Props = {}

const Features = ({}: Props) => {
  const { t } = useTranslation('homeLanding/features')

  // Helper function to safely get array from translations
  const getFeatures = (key: string): string[] => {
    const features = t(key, { returnObjects: true })
    return Array.isArray(features) ? features : []
  }

  const tabsContent = [
    {
      title: t('tabs.sellCourses.title'),
      description: t('tabs.sellCourses.description'),
      features: getFeatures('tabs.sellCourses.features'),
      subTabs: [
        {
          key: 'drag-drop',
          title: t('tabs.sellCourses.subTabs.dragDrop.title'),
          content: {
            description: t('tabs.sellCourses.subTabs.dragDrop.description'),
            content: (
              <Image
                src="/static/images/features/drag-drop.jpg"
                width={600}
                height={300}
                alt={t('tabs.sellCourses.subTabs.dragDrop.alt')}
              />
            ),
          },
        },
        {
          key: 'pricing',
          title: t('tabs.sellCourses.subTabs.pricing.title'),
          content: {
            description: t('tabs.sellCourses.subTabs.pricing.description'),
            content: (
              <Image
                src="/static/images/features/pricing.jpg"
                width={600}
                height={300}
                alt={t('tabs.sellCourses.subTabs.pricing.alt')}
              />
            ),
          },
        },
        {
          key: 'bundles',
          title: t('tabs.sellCourses.subTabs.bundles.title'),
          content: {
            description: t('tabs.sellCourses.subTabs.bundles.description'),
            content: (
              <Image
                src="/static/images/features/bundles.jpg"
                width={600}
                height={300}
                alt={t('tabs.sellCourses.subTabs.bundles.alt')}
              />
            ),
          },
        },
        {
          key: 'analytics',
          title: t('tabs.sellCourses.subTabs.analytics.title'),
          content: {
            description: t('tabs.sellCourses.subTabs.analytics.description'),
            content: (
              <Image
                src="/static/images/features/analytics.jpg"
                width={600}
                height={300}
                alt={t('tabs.sellCourses.subTabs.analytics.alt')}
              />
            ),
          },
        },
      ],
    },
    {
      title: t('tabs.launchSubscriptions.title'),
      description: t('tabs.launchSubscriptions.description'),
      features: getFeatures('tabs.launchSubscriptions.features'),
      subTabs: [
        {
          key: 'content-library',
          title: t('tabs.launchSubscriptions.subTabs.contentLibrary.title'),
          content: {
            description: t(
              'tabs.launchSubscriptions.subTabs.contentLibrary.description'
            ),
            content: (
              <Image
                src="/static/images/features/content-library.jpg"
                width={600}
                height={300}
                alt={t('tabs.launchSubscriptions.subTabs.contentLibrary.alt')}
              />
            ),
          },
        },
        {
          key: 'subscription-plans',
          title: t('tabs.launchSubscriptions.subTabs.subscriptionPlans.title'),
          content: {
            description: t(
              'tabs.launchSubscriptions.subTabs.subscriptionPlans.description'
            ),
            content: (
              <Image
                src="/static/images/features/subscription-plans.jpg"
                width={600}
                height={300}
                alt={t(
                  'tabs.launchSubscriptions.subTabs.subscriptionPlans.alt'
                )}
              />
            ),
          },
        },
        {
          key: 'free-previews',
          title: t('tabs.launchSubscriptions.subTabs.freePreviews.title'),
          content: {
            description: t(
              'tabs.launchSubscriptions.subTabs.freePreviews.description'
            ),
            content: (
              <Image
                src="/static/images/features/free-previews.jpg"
                width={600}
                height={300}
                alt={t('tabs.launchSubscriptions.subTabs.freePreviews.alt')}
              />
            ),
          },
        },
      ],
    },
    {
      title: t('tabs.monetizeFreeContent.title'),
      description: t('tabs.monetizeFreeContent.description'),
      features: getFeatures('tabs.monetizeFreeContent.features'),
      subTabs: [
        {
          key: 'thanks-donations',
          title: t('tabs.monetizeFreeContent.subTabs.thanksDonations.title'),
          content: {
            description: t(
              'tabs.monetizeFreeContent.subTabs.thanksDonations.description'
            ),
            content: (
              <Image
                src="/static/images/features/thanks-donations.jpg"
                width={600}
                height={300}
                className="object-cover"
                alt={t('tabs.monetizeFreeContent.subTabs.thanksDonations.alt')}
              />
            ),
          },
        },
      ],
    },
    {
      title: t('tabs.audioTranslations.title'),
      description: t('tabs.audioTranslations.description'),
      features: getFeatures('tabs.audioTranslations.features'),
      subTabs: [
        {
          key: 'multiple-audio',
          title: t('tabs.audioTranslations.subTabs.multipleAudio.title'),
          content: {
            description: t(
              'tabs.audioTranslations.subTabs.multipleAudio.description'
            ),
            content: (
              <Image
                src="/static/images/features/multiple-audio.jpg"
                width={600}
                height={300}
                alt={t('tabs.audioTranslations.subTabs.multipleAudio.alt')}
              />
            ),
          },
        },
      ],
    },
    {
      title: t('tabs.buildCommunity.title'),
      description: t('tabs.buildCommunity.description'),
      features: getFeatures('tabs.buildCommunity.features'),
      subTabs: [
        {
          key: 'messages-hub',
          title: t('tabs.buildCommunity.subTabs.messagesHub.title'),
          content: {
            description: t(
              'tabs.buildCommunity.subTabs.messagesHub.description'
            ),
            content: (
              <Image
                src="/static/images/features/messages-hub.jpg"
                width={600}
                height={300}
                alt={t('tabs.buildCommunity.subTabs.messagesHub.alt')}
              />
            ),
          },
        },
      ],
    },
  ]
  return (
    <div className="flex flex-col gap-5 min-h-[800px] py-12 max-w-7xl mx-auto px-4 justify-center items-center">
      <span className="text-[48px] text-uguPurple font-bold">{t('title')}</span>
      <Tabs
        fullWidth
        color="primary"
        classNames={{
          tab: 'font-bold text-base sm:text-lg md:px-0 h-16 bg-transparent',
          tabList: 'border-b-2 border-gray-200 p-0',
          cursor: 'w-full h-0.5',
          panel:
            'flex justify-between gap-5 md:flex-row flex-col md:px-6 !m-0 w-full',
        }}
        variant="underlined"
      >
        {tabsContent.map((tab) => (
          <Tab
            key={tab.title.toLowerCase().replace(' ', '-')}
            title={tab.title}
          >
            <div className="flex flex-col gap-6 flex-1">
              <span className="font-bold text-3xl">{tab.title}</span>
              <span className="text-[22px] font-regular max-w-[20rem]">
                {tab.description}
              </span>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                {tab.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center text-[22px] gap-2 font-regular"
                  >
                    <TickIcon height="10" width="12" /> {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className={`min-h-[401px] md:h-[461px] flex-1`}>
              {tab.subTabs.length > 1 ? (
                <Tabs
                  placement="bottom"
                  color="primary"
                  disableAnimation={true}
                  classNames={{
                    tab: 'bg-transparent font-medium text-base bg-transparent',
                    base: 'max-w-[591px]',
                  }}
                  fullWidth
                >
                  {tab.subTabs.map((subTab) => (
                    <Tab
                      key={subTab.title.toLowerCase().replace(' ', '-')}
                      title={subTab.title}
                    >
                      <div className="max-w-full flex flex-col gap-2">
                        <div className="max-w-full">
                          <p className="font-medium text-[20px] text-center">
                            {subTab.content.description}
                          </p>
                        </div>
                        <div>{subTab.content.content}</div>
                      </div>
                    </Tab>
                  ))}
                </Tabs>
              ) : (
                <div className="h-full">{tab.subTabs[0].content.content}</div>
              )}
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  )
}

export default Features
